import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Waves, Sun, Trees } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import PageHero from "@/components/site/PageHero";
import FinalCTA from "@/components/site/FinalCTA";

const MAP_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/db6a4cdca2b1655caa2657ceea8b35c9a1eacadba1089b77d7616aa2935bd06b.png";

const AREAS = [
  {
    slug: "cape-coral",
    name: "Cape Coral",
    icon: Waves,
    blurb:
      "A canal-laced coastal city where salt air and year-round humidity put extra demand on outdoor units, drain lines, and corrosion-prone components.",
  },
  {
    slug: "fort-myers",
    name: "Fort Myers",
    icon: Sun,
    blurb:
      "Older neighborhoods sit alongside newer builds, so HVAC needs swing from full system replacements to tune-ups on modern variable-speed systems.",
  },
  {
    slug: "naples",
    name: "Naples",
    icon: Sun,
    blurb:
      "Hot summers, mild winters, and a strong mix of single-family homes and condos — comfort, humidity control, and quiet operation matter equally.",
  },
  {
    slug: "bonita-springs",
    name: "Bonita Springs",
    icon: Waves,
    blurb:
      "Coastal influence and dense rainy seasons make condensate management and clean filtration a regular part of every visit.",
  },
  {
    slug: "estero",
    name: "Estero",
    icon: Trees,
    blurb:
      "A mix of master-planned communities and family neighborhoods that benefit from proactive maintenance plans and smart thermostat setup.",
  },
  {
    slug: "lee-county",
    name: "Lee County",
    icon: MapPin,
    blurb:
      "From Cape Coral and Fort Myers out to Estero and beyond, Lee County covers a wide footprint of cooling needs in coastal Southwest Florida.",
  },
  {
    slug: "collier-county",
    name: "Collier County",
    icon: MapPin,
    blurb:
      "Naples through Marco Island and inland — long cooling seasons and varied home styles where right-sized systems make a real difference.",
  },
];

const SERVICE_LINKS = [
  { to: "/services/ac-repair", label: "AC Repair" },
  { to: "/services/maintenance", label: "Maintenance" },
  { to: "/services/replacement", label: "Replacement" },
  { to: "/services/indoor-air-quality", label: "Indoor Air Quality" },
];

const PROCESS_STEPS = [
  "Tell us what’s happening",
  "Choose urgency and location",
  "Receive a callback or next-step message",
  "Contractor gets organized lead details",
];

export default function ServiceAreas() {
  useSeo({
    title:
      "Service areas in Southwest Florida | Sun Chaser Cooling — PrismForge Digital Demo",
    description:
      "Demo coverage map for Cape Coral, Fort Myers, Naples, Bonita Springs, Estero, Lee County, and Collier County — built by PrismForge Digital.",
  });

  return (
    <div data-testid="service-areas-page">
      <PageHero
        eyebrow="Coverage"
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Service Areas" }]}
        title="Serving"
        highlight="Southwest Florida."
        subtitle="From Cape Coral down through Collier County — the coast, the canals, and the neighborhoods where Florida humidity hits hardest."
        image={MAP_IMG}
        imageAlt="Stylized coastal map of Southwest Florida highlighting the Sun Chaser Cooling service area."
        primaryCta={{ label: "Request HVAC Service", to: "/request-service" }}
        secondaryCta={{
          label: "Schedule Maintenance",
          to: "/request-service?intent=maintenance",
        }}
        variant="light"
        testId="service-areas-page-hero"
      />

      <section
        data-testid="service-areas-list-section"
        aria-labelledby="areas-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <MapPin className="h-3 w-3 text-aqua" />
              Where we work
            </span>
            <h2
              id="areas-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Seven areas across Southwest Florida.
            </h2>
            <p className="mt-4 text-base text-navy/70 sm:text-lg">
              Each block links into the same smarter service-request flow —
              urgency, system age, what’s happening, and where you are.
            </p>
          </div>

          <motion.ul
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.05 }}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.05 } },
            }}
            className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2"
          >
            {AREAS.map((a) => {
              const Icon = a.icon;
              return (
                <motion.li
                  key={a.slug}
                  variants={{
                    hidden: { opacity: 0, y: 14 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
                  }}
                  data-testid={`service-area-block-${a.slug}`}
                  className="relative flex h-full flex-col rounded-2xl border border-mist-line bg-white p-6 shadow-soft transition-transform hover:-translate-y-1 sm:p-7"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-2xl font-bold leading-tight tracking-tight text-navy">
                      {a.name}
                    </h3>
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-aqua">
                      <Icon className="h-4 w-4" />
                    </span>
                  </div>
                  <p className="mt-3 text-[15px] leading-relaxed text-navy/70">
                    {a.blurb}
                  </p>

                  <div className="mt-5 border-t border-mist-line pt-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/55">
                      Services offered
                    </p>
                    <ul className="mt-3 flex flex-wrap gap-2">
                      {SERVICE_LINKS.map((s) => (
                        <li key={s.to}>
                          <Link
                            to={s.to}
                            data-testid={`service-area-${a.slug}-link-${s.label
                              .toLowerCase()
                              .replace(/\s+/g, "-")}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-mist-line bg-mist px-3 py-1 text-xs font-medium text-navy/80 transition-colors hover:border-aqua/45 hover:text-navy"
                          >
                            <span className="h-1 w-1 rounded-full bg-aqua" />
                            {s.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to={`/request-service?area=${a.slug}`}
                    data-testid={`service-area-${a.slug}-cta`}
                    className="mt-6 inline-flex items-center justify-between gap-2 rounded-full bg-navy px-4 py-2.5 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-navy-soft"
                  >
                    Request service in {a.name}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </motion.li>
              );
            })}
          </motion.ul>
        </div>
      </section>

      <section
        data-testid="service-areas-process-section"
        aria-labelledby="areas-process-heading"
        className="relative bg-mist py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
              How it works
            </span>
            <h2
              id="areas-process-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              The same clear process, anywhere in SWFL.
            </h2>
          </div>
          <ol className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS_STEPS.map((step, i) => (
              <li
                key={step}
                className="flex flex-col rounded-2xl border border-mist-line bg-white p-5 shadow-soft"
              >
                <span className="font-display text-3xl font-bold leading-none text-mist-line">
                  0{i + 1}
                </span>
                <p className="mt-4 font-display text-base font-bold leading-snug text-navy">
                  {step}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <FinalCTA
        title="Anywhere from Cape Coral to Collier County — start with a clear request."
        subtitle="Tell us where you are and what's happening. The intake flow handles the rest."
      />
    </div>
  );
}
